import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProjectStore } from '../store/useProjectStore';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { IndicatorService } from '../services/indicatorService';
import { ActionService } from '../services/actionService';
import type { Indicator } from '../types/indicator';
import type { SustainabilityAudit } from '../types/consultingProject';

const EMPTY_KPI_BINDINGS: Array<{ lastReadingDate: string }> = [];

export type Step5CtaTarget = 'actions' | 'kpi' | 'audit';

export interface Step5EvidenceCheck {
  id: Step5CtaTarget;
  label: string;
  isValid: boolean;
  detail: string;
  ctaLabel: string;
  ctaTarget: Step5CtaTarget;
}

export interface Step5EvidenceResult {
  totalEvidencias: number;
  checks: Step5EvidenceCheck[];
  canConclude: boolean;
  blockMessage: string | null;
  isLoading: boolean;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedValue(value), delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [value, delayMs]);

  return debouncedValue;
}

function startOfCurrentMonthIso(now = new Date()): string {
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  return start.toISOString();
}

function formatRelativeDays(referenceIso?: string | null): string {
  if (!referenceIso) return 'sem leitura recente';
  const date = new Date(referenceIso);
  if (Number.isNaN(date.getTime())) return 'data inválida';

  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  if (diffDays === 0) return 'hoje';
  if (diffDays === 1) return '1 dia';
  return `${diffDays} dias`;
}

export function useStep5Evidences(projectId?: string | null, reviews: SustainabilityAudit[] = []): Step5EvidenceResult {
  const organizationId = useOrganizationId();
  const localProjectKpisRaw = useProjectStore((state) => {
    if (!projectId) return undefined;
    const project = state.projects.find((item) => item.id === projectId);
    return project?.estado_atual?.kpis;
  });
  const localProjectKpis = localProjectKpisRaw ?? EMPTY_KPI_BINDINGS;

  const { data: enterpriseProjectActions = [], isLoading: isEnterpriseActionsLoading } = useQuery({
    queryKey: ['step5-evidences-actions', organizationId, projectId],
    queryFn: () => ActionService.list(organizationId!, { project_id: projectId! }),
    enabled: !!organizationId && !!projectId,
    // Mantido curto para refletir mutações da Central de Ações sem atrasos perceptíveis.
    staleTime: 5_000,
  });

  const projectActions = useMemo(() => {
    if (!projectId) return [];

    // Sincronização ESTRITA com a Central de Ações:
    // - fonte única: enterprise actions filtradas por project_id
    // - escopo: ações VSM (origem === 'VSM') — qualquer marcador de origem VSM é aceito
    //   para cobrir variações históricas (sourceComponentId, metadata.source, metadata.origin, generatedFrom)
    const isVsmOrigin = (metadata: Record<string, unknown> | null | undefined): boolean => {
      if (!metadata || typeof metadata !== 'object') return false;
      const sourceComponentId = (metadata as { sourceComponentId?: unknown }).sourceComponentId;
      if (typeof sourceComponentId === 'string' && sourceComponentId.length > 0) return true;
      const candidates = [
        (metadata as { source?: unknown }).source,
        (metadata as { origin?: unknown }).origin,
        (metadata as { origem?: unknown }).origem,
        (metadata as { generatedFrom?: unknown }).generatedFrom,
      ];
      return candidates.some((value) => typeof value === 'string' && value.toLowerCase() === 'vsm');
    };

    const acoesDoProjeto = enterpriseProjectActions
      .filter((action) => action.project_id === projectId)
      .filter((action) => isVsmOrigin(action.metadata as Record<string, unknown> | null | undefined))
      .filter((action) => action.status !== 'cancelled');

    return acoesDoProjeto.map((action) => ({
      id: action.id,
      title: action.title,
      status:
        action.status === 'completed'
          ? 'done'
          : action.status === 'in_progress'
            ? 'doing'
            : 'todo',
      projectId: action.project_id ?? undefined,
      actionPlanId: action.plan_id ?? undefined,
      origin: 'canvas' as const,
      linkedNodeId: typeof action.metadata?.linkedNodeId === 'string' ? action.metadata.linkedNodeId : undefined,
      sourceComponentId: typeof action.metadata?.sourceComponentId === 'string' ? action.metadata.sourceComponentId : undefined,
      deadline: action.due_date ?? undefined,
      prazo: action.due_date ?? undefined,
      priority: action.priority ?? undefined,
      createdAt: action.created_at,
      updatedAt: action.updated_at,
    }));
  }, [enterpriseProjectActions, projectId]);

  const debouncedActions = useDebouncedValue(projectActions, 150);
  const debouncedReviews = useDebouncedValue(reviews, 150);

  const { data: projectIndicators = [], isLoading: isIndicatorsLoading } = useQuery({
    queryKey: ['step5-evidences-indicators', organizationId, projectId],
    queryFn: () => IndicatorService.list(organizationId!, { project_id: projectId! }),
    enabled: !!organizationId && !!projectId,
    staleTime: 60_000,
  });

  const indicatorIds = useMemo(() => projectIndicators.map((indicator) => indicator.id), [projectIndicators]);

  const { data: latestSnapshotMap = {}, isLoading: isSnapshotLoading } = useQuery({
    queryKey: ['step5-evidences-snapshots', organizationId, projectId, indicatorIds],
    queryFn: () => IndicatorService.getLatestSnapshotMap(organizationId!, indicatorIds),
    enabled: !!organizationId && !!projectId && indicatorIds.length > 0,
    staleTime: 60_000,
  });

  const debouncedIndicators = useDebouncedValue(projectIndicators, 150);
  const debouncedSnapshotMap = useDebouncedValue(latestSnapshotMap, 150);
  const debouncedLocalKpis = useDebouncedValue(localProjectKpis, 150);

  return useMemo(() => {
    const today = new Date();
    const currentMonthStartIso = startOfCurrentMonthIso(today);
    const nowTime = today.getTime();

    const totalActions = debouncedActions.length;
    const completedActions = debouncedActions.filter((action) => action.status === 'done').length;
    const completionRate = totalActions > 0 ? completedActions / totalActions : 0;
    const criticalOverdueActions = debouncedActions.filter((action) => {
      if (action.status === 'done' || action.status === 'canceled') return false;
      const deadline = action.deadline ?? action.prazo;
      if (!deadline) return false;
      const dueTime = new Date(deadline).getTime();
      if (Number.isNaN(dueTime) || dueTime >= nowTime) return false;
      const priority = String(action.priority ?? '').toLowerCase();
      return priority === 'high' || priority === 'urgent';
    });

    const hasActions = totalActions > 0;
    const isAcoesValidas = hasActions && completionRate >= 0.8 && criticalOverdueActions.length === 0;

    const latestSnapshotReadingIso = Object.values(debouncedSnapshotMap)
      .filter(Boolean)
      .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] ?? null;
    const latestLocalReadingIso = debouncedLocalKpis
      .map((kpi) => kpi.lastReadingDate)
      .filter(Boolean)
      .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] ?? null;

    const mergedIndicatorCount = debouncedIndicators.length + debouncedLocalKpis.length;
    const latestReadingIso = [latestSnapshotReadingIso, latestLocalReadingIso]
      .filter(Boolean)
      .sort((left, right) => new Date(right as string).getTime() - new Date(left as string).getTime())[0] ?? null;

    const latestReadingTime = latestReadingIso ? new Date(latestReadingIso).getTime() : NaN;
    const hasRecentReading = Number.isFinite(latestReadingTime) && (nowTime - latestReadingTime) <= 30 * 24 * 60 * 60 * 1000;
    const isKpiValido = mergedIndicatorCount > 0 && hasRecentReading;

    const reviewThisMonth = debouncedReviews.filter((review) => {
      const auditDateIso = review.auditDate ?? review.createdAt;
      return Boolean(auditDateIso) && new Date(auditDateIso).getTime() >= new Date(currentMonthStartIso).getTime();
    });
    const isRevisaoValida = reviewThisMonth.length > 0;

    const checks: Step5EvidenceCheck[] = [
      {
        id: 'actions',
        label: 'Monitorar Execução',
        isValid: isAcoesValidas,
        detail: hasActions
          ? `${completedActions}/${totalActions} ações concluídas${criticalOverdueActions.length > 0 ? ` • ${criticalOverdueActions.length} atrasadas críticas` : ''}`
          : 'Sem ações vinculadas ao projeto',
        ctaLabel: 'Ver Ações',
        ctaTarget: 'actions',
      },
      {
        id: 'kpi',
        label: 'Medir Resultados',
        isValid: isKpiValido,
        detail: mergedIndicatorCount > 0
          ? `Última leitura: ${formatRelativeDays(latestReadingIso)}`
          : 'Nenhum KPI vinculado ao projeto',
        ctaLabel: 'Vincular KPI',
        ctaTarget: 'kpi',
      },
      {
        id: 'audit',
        label: 'Sustentar Ganhos',
        isValid: isRevisaoValida,
        detail: isRevisaoValida
          ? `${reviewThisMonth.length} revisão(ões) neste mês`
          : 'Sem revisão mensal registrada',
        ctaLabel: 'Auditar Fluxo',
        ctaTarget: 'audit',
      },
    ];

    const totalEvidencias = checks.filter((check) => check.isValid).length;
    const blockMessage = totalEvidencias === 3
      ? null
      : `Faltam evidências de sustentação: ${checks.filter((check) => !check.isValid).map((check) => check.label).join(', ')}.`;

    return {
      totalEvidencias,
      checks,
      canConclude: totalEvidencias === 3,
      blockMessage,
      isLoading: isIndicatorsLoading || isSnapshotLoading || isEnterpriseActionsLoading,
    } satisfies Step5EvidenceResult;
  }, [
    debouncedActions,
    debouncedIndicators,
    debouncedLocalKpis,
    debouncedReviews,
    debouncedSnapshotMap,
    isEnterpriseActionsLoading,
    isIndicatorsLoading,
    isSnapshotLoading,
  ]);
}